import {
  type FeatureFlagValues,
  featureFlagValuesSchema,
} from '@his/feature-flags';
import { type QueryFunctionContext, queryOptions } from '@tanstack/react-query';
import { getCurrentUser } from '@/integrations/idb/models/users';
import { Sentry } from '@/integrations/sentry/sentry';
import { FEATURE_FLAGS_CONFIG } from '@/lib/env';

const FEATURE_FLAGS_PATH = '/v1/feature-flags' as const;
export const FEATURE_FLAGS_QUERY_KEY = ['feature-flags'] as const;

type FeatureFlagsFailure = 'session' | 'network' | 'http' | 'json' | 'schema';

class FeatureFlagsQueryError extends Error {
  constructor(failure: FeatureFlagsFailure, status?: number) {
    super(
      status === undefined
        ? `Feature flag request failed: ${failure}`
        : `Feature flag request failed: ${failure} (${status})`,
    );
    this.name = 'FeatureFlagsQueryError';
  }
}

const reportFailure = (failure: FeatureFlagsFailure, status?: number) => {
  const error = new FeatureFlagsQueryError(failure, status);
  Sentry.captureException(error, {
    tags: {
      feature: 'feature-flags',
      failure,
      ...(status === undefined ? {} : { status: String(status) }),
    },
  });
  return error;
};

const getFeatureFlagsUrl = () => {
  if (FEATURE_FLAGS_CONFIG.mode !== 'remote') {
    throw reportFailure('session');
  }

  return `${FEATURE_FLAGS_CONFIG.endpoint.replace(/\/+$/, '')}${FEATURE_FLAGS_PATH}`;
};

const queryFn = async ({ signal }: QueryFunctionContext) => {
  let token: string;
  let tenantId: string;
  try {
    const currentUser = await getCurrentUser();
    if (!(currentUser?.token && currentUser.tenantId)) {
      throw reportFailure('session');
    }
    token = currentUser.token;
    tenantId = currentUser.tenantId;
  } catch (error) {
    if (error instanceof FeatureFlagsQueryError) {
      throw error;
    }
    throw reportFailure('session');
  }

  let response: Response;
  try {
    response = await fetch(getFeatureFlagsUrl(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
      signal,
    });
  } catch {
    throw reportFailure('network');
  }

  if (!response.ok) {
    throw reportFailure('http', response.status);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw reportFailure('json');
  }

  try {
    return featureFlagValuesSchema.parse(payload);
  } catch {
    throw reportFailure('schema');
  }
};

export const getFeatureFlagsQueryOptions = () =>
  queryOptions<FeatureFlagValues>({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    throwOnError: true,
    meta: { persist: false },
  });
